/**
 * Safety-form field logic shared by the builder, its live preview and the
 * renderer (DynamicSafetyFormScreen): option shapes, show_if rules, and which
 * answers a submission carries.
 *
 * The API shapes (feat/safety-forms-conditional-api):
 *   options.choices   Array<string | { value, description? }>
 *   options.layout    'inline' | absent (stacked)
 *   conditional_logic { show_if: { field_id, equals } }        — legacy, one value
 *                     { show_if: { field_id, any_of: [...] } } — the builder writes this
 */

export type FormChoice = string | { value: string; description?: string | null };

export interface NormalizedChoice {
  value: string;
  description: string | null;
}

export type ShowIf =
  | { field_id: string; equals: string }
  | { field_id: string; any_of: string[] };

export interface ConditionalField {
  id: string;
  field_type: string;
  conditional_logic?: { show_if?: Partial<ShowIf> & { field_id?: string } } | null;
}

/** Field types whose options are a list of choices. */
export const CHOICE_FIELD_TYPES: ReadonlySet<string> = new Set(['dropdown', 'checkbox_group', 'radio']);

/** Field types that can render their choices in one row. */
export const INLINE_LAYOUT_TYPES: ReadonlySet<string> = new Set(['checkbox_group', 'radio']);

/**
 * Field types a rule can point at: the parent's answer has to be one value
 * (or a set of values) out of a known list, or a Yes/No.
 */
export const RULE_PARENT_TYPES: ReadonlySet<string> = new Set(['radio', 'dropdown', 'checkbox_group', 'yes_no']);

export function normalizeChoice(choice: unknown): NormalizedChoice | null {
  if (typeof choice === 'string') return { value: choice, description: null };
  if (choice && typeof choice === 'object' && typeof (choice as any).value === 'string') {
    const d = (choice as any).description;
    return { value: (choice as any).value, description: typeof d === 'string' && d.trim() ? d : null };
  }
  return null;
}

export function normalizeChoices(options: unknown): NormalizedChoice[] {
  const raw = (options as any)?.choices;
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeChoice).filter((c): c is NormalizedChoice => c !== null);
}

/**
 * Back to the wire shape: a plain string when there is no description, so a
 * template that never uses descriptions saves byte-for-byte as before.
 */
export function serializeChoices(choices: NormalizedChoice[]): FormChoice[] {
  return choices.map(c =>
    c.description && c.description.trim() ? { value: c.value, description: c.description } : c.value,
  );
}

/** The values a parent field's answer can take, for the rule editor. */
export function ruleValuesFor(field: { field_type: string; options?: unknown }): string[] {
  if (field.field_type === 'yes_no') return ['Yes', 'No'];
  return normalizeChoices(field.options).map(c => c.value);
}

/** The values a rule matches, whichever of its two shapes it is in. */
export function ruleValues(showIf: unknown): string[] {
  if (!showIf || typeof showIf !== 'object') return [];
  const s = showIf as any;
  if (Array.isArray(s.any_of)) return s.any_of.map(String);
  if (s.equals !== undefined && s.equals !== null) return [String(s.equals)];
  return [];
}

/** True when the parent's answer satisfies the rule. A checkbox group matches when any ticked value is listed. */
function answerMatches(answer: unknown, values: string[]): boolean {
  if (values.length === 0) return false;
  if (Array.isArray(answer)) return answer.some(a => values.includes(String(a)));
  if (answer === undefined || answer === null || answer === '') return false;
  return values.includes(String(answer));
}

/**
 * Whether a field is shown, given the current answers.
 *
 * A field whose parent is itself hidden is hidden too, even if the parent
 * still holds an answer from before it was hidden. Otherwise un-picking a
 * grandparent would leave a grandchild on screen because of a stale value.
 * A rule pointing at a field that is not on the template hides the field.
 * The builder cannot write one, so a stale reference means the template has
 * changed under it, and showing the field would claim a rule that no longer
 * exists.
 */
export function isFieldVisible(
  field: ConditionalField,
  answers: Record<string, unknown>,
  byId: Map<string, ConditionalField>,
  seen: Set<string> = new Set(),
): boolean {
  const showIf = field.conditional_logic?.show_if;
  if (!showIf || !showIf.field_id) return true;
  if (seen.has(field.id)) return false; // a cycle can never be satisfied
  const parent = byId.get(showIf.field_id);
  if (!parent) return false;
  seen.add(field.id);
  if (!isFieldVisible(parent, answers, byId, seen)) return false;
  return answerMatches(answers[parent.id], ruleValues(showIf));
}

export function indexFields<T extends ConditionalField>(fields: T[]): Map<string, T> {
  return new Map(fields.map(f => [f.id, f]));
}

/** The answers to send: hidden fields' answers are dropped, not sent as blanks. */
export function visibleAnswers(
  fields: ConditionalField[],
  answers: Record<string, unknown>,
): Record<string, unknown> {
  const byId = indexFields(fields);
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(answers)) {
    const f = byId.get(key);
    if (f && !isFieldVisible(f, answers, byId)) continue;
    out[key] = value;
  }
  return out;
}

/** Fields that would lose their rule's parent if `fieldId` were deleted. */
export function dependentsOf(fieldId: string, fields: ConditionalField[]): ConditionalField[] {
  return fields.filter(f => f.conditional_logic?.show_if?.field_id === fieldId);
}

/**
 * A new field's id. A real UUID, so a show_if written against a field that
 * has never been saved still names it after the save. The API keeps an id it
 * is given (feat/safety-forms-conditional-api).
 */
export function newFieldId(): string {
  const c: Crypto | undefined = (globalThis as any).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // RFC 4122 v4 from getRandomValues, for runtimes without randomUUID.
  const b = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
